import type { ReactNode } from "react";
import type { GameStateView } from "@shared/types";
import { useT } from "../lib/i18n";
import { Icon } from "./Icon";

/**
 * Shared standings: between rounds and at the end of the game. `action` is the
 * host's button — players and the TV pass nothing, so the same component serves
 * all three screens without a role flag.
 */
export function Standings({ game, action }: { game: GameStateView; action?: ReactNode }) {
  const t = useT();
  const isFinal = game.phase === "final";
  const players = game.players; // already sorted by score, descending
  const winner = players[0];
  const top = players[0]?.score ?? 0;

  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-8 p-6 overflow-y-auto">
      <header className="text-center">
        <div className="type-eyebrow text-accent">
          {isFinal ? t("finalScores") : t("standings")}
        </div>
        <h2 className="type-display text-4xl sm:text-5xl text-accentLight mt-2">
          {isFinal ? t("gameOver") : t("roundOver", { round: game.roundName })}
        </h2>
        {isFinal && winner && (
          <p className="text-xl mt-3 inline-flex items-center gap-2 text-ink/70">
            <Icon name="trophy" className="text-accentLight" />
            {t("winner")}: <span className="font-semibold text-ink">{winner.name}</span>
          </p>
        )}
      </header>

      <ol className="w-full max-w-md flex flex-col gap-2">
        {players.length === 0 && (
          <li className="text-ink/40 text-center py-6">{t("noPlayers")}</li>
        )}
        {players.map((p, i) => {
          const lead = i === 0 && p.score > 0;
          // A quiet bar behind each row turns a column of numbers into a shape
          // you can read from across the room.
          const width = top > 0 ? Math.max(0, (p.score / top) * 100) : 0;
          return (
            <li
              key={p.id}
              className={`relative flex items-center gap-3 rounded-card p-3 overflow-hidden ${
                lead ? "bg-accent text-onAccent" : "bg-ink/[0.07]"
              }`}
            >
              {!lead && width > 0 && (
                <span
                  aria-hidden
                  className="absolute inset-y-0 left-0 bg-ink/[0.06]"
                  style={{ width: `${width}%` }}
                />
              )}
              <span
                className={`relative type-code text-sm w-6 text-center ${
                  lead ? "opacity-60" : "text-ink/35"
                }`}
              >
                {i + 1}
              </span>
              <img
                src={p.avatarUrl}
                alt=""
                className="relative w-10 h-10 rounded-control bg-white shrink-0"
              />
              <span className="relative flex-1 font-semibold truncate">{p.name}</span>
              <span
                className={`relative text-xl font-bold tabular ${
                  !lead && p.score < 0 ? "text-danger" : ""
                }`}
              >
                {p.score}
              </span>
            </li>
          );
        })}
      </ol>

      {action}
    </div>
  );
}
